import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextInShape } from "@companion-core/shared/app/Components/TextInShape";
import { getModule } from "@companion-core/shared/app/Utils/config";
import { CommunicationChannel, getInitialChannels } from "./BellHome.model";
import { useMemberData } from "@companion-core/shared/app/Hooks/member";
import { getPhoneNumberFromSubscriptions } from "@companion-core/shared/app/Utils/memberHelper";
import { useBellStore } from "@companion-core/shared/app/Store/bellStore";
import { trackClickBell, trackHomePageBell } from "@companion-core/shared/app/tracking/bell";
import { redirectTo } from "@companion-core/web/src/Utilities/navigation";
import { PopIn } from "@companion-core/shared/app/Components/PopIn";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { Tooltip } from "@companion-core/web/src/Components/Tooltip";
import "@companion-core/web/src/Assets/Styles/scss/bell.scss";
import { useMountEffect } from "@companion-core/shared/app/Hooks/useMountEffect";

export const BellHome = () => {
  const { t } = useTranslation();
  const { data: member } = useMemberData();
  const subscriptions = useMemo(() => member?.subscriptions, [member]);
  const bellHomeOptions = getModule("concierge")?.modules.bell.options.home;
  const homeEntries = bellHomeOptions.channels;
  const whatsAppLink = bellHomeOptions?.whatsAppLink;
  const { isPopUpVisible, setBellContent, setHeaderAccessibilityLabel } = useBellStore((state) => ({
    isPopUpVisible: state.isPopUpVisible,
    setBellContent: state.setBellContent,
    setHeaderAccessibilityLabel: state.setHeaderAccessibilityLabel,
  }));
  const [isPhonePopinOpened, setIsPhonePopinOpened] = useState(false);
  const homeSubtitleRef = useRef<HTMLHeadingElement>(null);
  const ask = () => {
    trackClickBell("Make_A_Request");
    setBellContent("categories");
  };
  const call = () => {
    trackClickBell("Concierge_call_request");
    setIsPhonePopinOpened(true);
  };
  const whatsApp = () => {
    trackClickBell("Concierge_whatsapp_request");
    redirectTo(whatsAppLink, "external");
  };
  const initialChannels: CommunicationChannel[] = getInitialChannels(
    homeEntries,
    ask,
    call,
    whatsApp
  );
  const [channels, setChannels] = useState<CommunicationChannel[]>(initialChannels);
  const enabledChannels: CommunicationChannel[] = channels.filter((channel) => channel.enabled);

  useMountEffect(() => {
    trackHomePageBell();
    homeSubtitleRef.current?.focus({ preventScroll: true });
  });

  useEffect(() => {
    setHeaderAccessibilityLabel(
      t("request.concierge.header.accessibility.title.home", {
        count: enabledChannels.length,
      })
    );
  }, [enabledChannels.length, isPopUpVisible, setHeaderAccessibilityLabel, t]);

  useEffect(() => {
    const phoneNumber = subscriptions?.length
      ? getPhoneNumberFromSubscriptions(subscriptions)
      : null;
    const newChannels: CommunicationChannel[] = channels.map((channel) => {
      if (channel.name === "call" && homeEntries?.call.enabled) {
        channel.enabled = !!phoneNumber;
      }
      return channel;
    });
    setChannels(newChannels);
  }, [subscriptions]);

  const PhonePopinContent = () => {
    const phone = getPhoneNumberFromSubscriptions(subscriptions ?? []) ?? "";
    return (
      <div className="bell__phone-popin-content">
        <div
          className="bell__phone-popin-content-close"
          data-testid="bell__phone-popin-content-close"
          onClick={() => setIsPhonePopinOpened(false)}>
          <FontIcon name="JP-Close-bold" size={24} />
        </div>
        <span className="bell__phone-popin-content-subtext body-text-6">
          {t("common.bell.phone-popin.title")}
        </span>
        <div className="bell__phone-popin-content-phone">
          <span className="body-text-3">{phone}</span>
          <Tooltip text={t("common.clipboard.text")} direction={"right"} timer={3000}>
            <FontIcon
              className="bell__phone-popin-content-copy-button"
              name="JP-Copy-document"
              size={24}
              onClick={() => phone && navigator.clipboard.writeText(phone)}
            />
          </Tooltip>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="bell__home" className="bell__home">
      <PopIn
        isOpen={isPhonePopinOpened}
        testID="bell__phone-popin"
        content={<PhonePopinContent />}
        onClose={() => setIsPhonePopinOpened(false)}
      />
      <h2 className="bell__home-title title-1">
        {t("requestV2.concierge.home.title", { firstName: member?.firstname ?? "" })}
      </h2>
      <h3 ref={homeSubtitleRef} tabIndex={-1} className="bell__home-subtitle title-6">
        {t("request.concierge.home.subtitle")}
      </h3>
      <ul className="bell__channels">
        {enabledChannels.map((channel) => (
          <li key={channel.name}>
            <TextInShape
              key={channel.name}
              testID={`bell__channel-button--${channel.name}`}
              shape="stretched"
              ariaLabel={t(`request.concierge.home.${channel.name}.ariaLabel`)}
              text={t(`request.concierge.home.${channel.name}.label`)}
              icon={channel.icon}
              value={channel.name}
              onClick={channel.callback}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};
